<template>
  <div v-if="accessType !== 'NoAccess'">
    <page-header
      :page="pageHeaderConfig"
      right-side-class="col-sm-12 col-md-4 align-items-end d-flex pb-2 justify-content-end"
    >
      <template v-slot:customActions>
        <dropdown
          v-if="isAdmin"
          class="reports-dropdown actions-dropdown"
          icon="fas fa-folders"
          :items="dropdownHeaderActions"
          responsive
          title="Informes"
        />
        <action-button
          v-if="isAdmin"
          class="mr-2 d-block"
          i="fas fa-key"
          responsive
          variant="outline-success"
          @click="goToTokenHistory"
        >
          Solicitud de Token
        </action-button>
        <action-button
          v-if="isAdmin"
          i="fas fa-users pr-1"
          responsive
          variant="success"
          @click="goToEvaluators"
        >
          Evaluadores
        </action-button>
      </template>
    </page-header>

    <div
      class="service-proposal-history__filter-manager-wrapper"
      :class="{
        'service-proposal-history__filter-manager-wrapper--provider': isProvider,
        'service-proposal-history__filter-manager-wrapper--isAdmin': isAdmin,
        'service-proposal-history__filter-manager-wrapper--evaluator': isEvaluator,
      }"
    >
      <filter-manager
        v-model="selectedFilters"
        class="service-proposal-history__filter-manager"
        :display-advanced="isAdmin"
        :filters="filtersConfig"
        :search-container-class="searchContainerClass"
        @search="search()"
        @update:openAdvanced="advanceSearchHandler"
      />
    </div>

    <custom-separator class="pt-3 my-4" text="Propuestas de Servicios" />

    <callout-list
      :access-type="accessType"
      :data="results"
      @on-proposal-status-change="search"
    />

    <custom-result-not-found
      v-if="results.length === 0"
      text="No hay información disponible"
    />

    <pagination
      v-if="results.length > 0 && pagination.totalItemCount > pageSize"
      :data="pagination"
      :show-text="true"
      @pagination-go-page="search"
    />
  </div>
</template>

<script>
import CustomSeparator from 'custom-separator';
import alertMixin from 'utils/mixins/alert.mixin';
import PageHeader from 'PageHeader';
import Pagination from 'pagination';
import FilterManager from 'FilterManager';
import CustomResultNotFound from 'custom-result-not-found';
import CalloutList from '@/views/provider/proposalHistory/components/CalloutList.vue';
import ActionButton from 'ActionButton';
import Dropdown from 'dropdown';
import { name as moduleName } from '@/modules/serviceProposalHistory/store';
import storeMixin from '@/shared/mixins/storeGenerator';
import {
  getSummaryEvaluationProcessReport,
  getEvaluatorProgressReport,
} from '@/modules/serviceProposalHistory/api';
import _generalApi from '@/modules/app/api';
import {
  getInitFilters,
  getProposalService,
  getEvaluatorsByYearId,
  getCompaniesByYearId,
  proposalsCanBeAssignedToEvaluators,
  validateProposalsAssignedToEvaluators,
} from '@/modules/serviceProposalHistory/api';

export default {
  name: 'ServiceProposalHistory',
  components: {
    PageHeader,
    CustomSeparator,
    Pagination,
    FilterManager,
    CustomResultNotFound,
    CalloutList,
    ActionButton,
    Dropdown,
  },
  mixins: [alertMixin, storeMixin],
  props: {
    accessType: {
      type: String,
      default: () => 'NoAccess',
    },
  },
  data: () => ({
    moduleName,
    pageHeaderConfig: {
      icon: 'fas fa-user-tie',
      title: 'Proceso de Evaluación',
      routes: ['Proveedor', 'Proceso de Evaluación'],
    },
    results: [],
    pagination: {
      totalItemCount: 0,
      count: 0,
      pageCount: 0,
      currentPage: 1,
    },
    pageSize: 10,
    proposal: {
      canBeAssignedToEvaluators: false,
    },
    selectedFilters: {
      academicYear: null,
      area: null,
      evaluator: null,
      provider: null,
      status: null,
      document: null,
      proposalNumber: null,
    },
    filters: {
      academicYears: [],
      areas: [],
      evaluators: [],
      providers: [],
      statusList: [],
      evaluationStatusList: [],
      serviceTypes: [],
      documents: [
        { id: true, name: 'Sometido' },
        { id: false, name: 'No sometido' },
      ],
      recruitmentTypes: [
        { id: 1, name: 'Compañia' },
        { id: 2, name: 'Entidad Educativa' },
        { id: 3, name: 'Individuo' },
      ],
    },
  }),
  computed: {
    isEvaluator() {
      return this.accessType === 'Evaluator';
    },
    isProvider() {
      return this.accessType === 'Provider';
    },
    isAdmin() {
      return this.accessType === 'Admin';
    },
    filtersConfig() {
      let filters = [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filters.academicYears,
          placeholder: 'Seleccione el año',
          fieldName: 'description',
          fieldKey: 'id',
          validations: 'required',
          clearable: false,
          class: this.isProvider
            ? 'my-md-0 p-md-0 pr-md-4 pl-md-3'
            : 'col-md-4',
          initialValue: this.selectedFilters.academicYear,
        },
      ];

      if (this.isEvaluator) {
        filters = filters.concat([
          {
            name: 'status',
            component: 'CustomDropDown',
            options: this.filters.evaluationStatusList,
            placeholder: 'Estatus',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            class: 'col-md-4',
          },
          {
            name: 'proposalNumber',
            component: 'CustomInput',
            placeholder: 'Escriba Número de Propuesta',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            class: 'col-md-4',
            returnModel: true,
          },
        ]);
      }

      if (this.isAdmin) {
        filters = [
          ...filters,
          {
            name: 'proposalNumber',
            component: 'CustomInput',
            placeholder: 'Escriba Número de Propuesta',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            class: 'col-md-8',
            returnModel: true,
          },
          // Advanced
          {
            label: 'Estatus',
            name: 'status',
            component: 'CustomDropDown',
            options: this.filters.statusList,
            placeholder: 'Estatus',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            isAdvanced: true,
            class: 'col-md-4',
          },
          {
            label: 'Documentos',
            name: 'document',
            component: 'CustomDropDown',
            options: this.filters.documents,
            placeholder: 'Documentos',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            isAdvanced: true,
            class: 'col-md-4',
          },
          {
            label: 'Evaluador',
            name: 'evaluator',
            component: 'CustomDropDown',
            options: this.filters.evaluators,
            placeholder: 'Evaluador',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            isAdvanced: true,
            class: 'col-md-4',
          },
          {
            label: 'Proponente',
            name: 'provider',
            component: 'CustomDropDown',
            options: this.filters.providers,
            placeholder: 'Proponente',
            fieldName: 'name',
            fieldKey: 'ssn',
            clearable: true,
            isAdvanced: true,
            class: 'col-md-3',
          },
          {
            label: 'Área',
            name: 'area',
            component: 'CustomDropDown',
            options: this.filters.areas,
            placeholder: 'Área',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            isAdvanced: true,
            class: 'col-md-3',
          },
          {
            label: 'Tipo de Proponente',
            name: 'recruitmentTypes',
            component: 'CustomDropDown',
            options: this.filters.recruitmentTypes,
            placeholder: 'Tipo de Proponente',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            isAdvanced: true,
            class: 'col-md-3',
          },
          {
            label: 'Servicios a ofrecer ',
            name: 'serviceTypes',
            component: 'CustomDropDown',
            options: this.filters.serviceTypes,
            placeholder: 'Tipo de Proponente',
            fieldName: 'name',
            fieldKey: 'id',
            clearable: true,
            isAdvanced: true,
            class: 'col-md-3',
          },
        ];
      }

      return filters;
    },
    dropdownHeaderActions() {
      return [
        {
          name: 'documents',
          label: 'Informe Resumen de Proceso de Evaluación de Propuesta',
          icon: 'fas fa-download text-green pr-1',
          visible: true,
          click: () => {
            this.downloadSummaryEvaluationProcessReport();
          },
        },
        {
          name: 'documentsProsesoEvaluador',
          label: 'Informe de Progreso Evaluadores',
          icon: 'fas fa-download text-green pr-1',
          visible: true,
          click: () => {
            this.downloadEvaluatorProgressReport();
          },
        },
      ];
    },
    searchContainerClass() {
      let classStr = 'service-proposal-history__filter-manager__search';

      if (this.isProvider) classStr += ' col-lg-4';

      return classStr;
    },
  },
  watch: {
    'selectedFilters.academicYear'(current, old) {
      if (old && current.id != old.id) {
        this.getEvaluatorsByYearId(current.id);
        this.getCompaniesByYearId(current.id);
      }
    },
  },
  async mounted() {
    await proposalsCanBeAssignedToEvaluators().then((res) => {
      this.proposal.canBeAssignedToEvaluators = res.data;
    });
  },
  async created() {
    await this.recoveryData();
    await this.initFilters();
    if (Number.parseInt(this.$route.params.academicYearId) > 0) {
      await this.updateProposalServiceEvaluations();
    }

    if (this.$route.params.reload) {
      await this.search();
    }
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    goToTokenHistory() {
      this.$router.push({ name: 'ServiceProposalTokenHistory' });
    },
    async initFilters() {
      if (this.isEvaluator) {
        this.pageSize = 20;
      }

      if (this.shouldRecovery && this.selectedFilters.academicYear) {
        return;
      }

      await getInitFilters().then((res) => {
        this.selectedFilters = {
          ...this.selectedFilters,
          ...res.data.filters,
        };

        const { collections } = res.data;
        this.filters = {
          ...this.filters,
          academicYears: collections.academicYears || [],
          areas: collections.areas || [],
          evaluators: collections.evaluators || [],
          providers: collections.providers || [],
          statusList: collections.statusList || [],
          evaluationStatusList: collections.evaluationStatusList || [],
          serviceTypes: collections.serviceTypes || [],
        };
      });
    },
    async search(page = 1) {
      this.pagination.currentPage = page;

      const filters = { ...this.selectedFilters };

      let params = {
        AcademicYearId: filters.academicYear ? filters.academicYear.id : null,
        CenterId: filters.area ? filters.area.id : null,
        ProposalRequestStatusId: this.isEvaluator
          ? null
          : filters.status
          ? filters.status.id
          : null,
        UserIsProposalAdmin: this.userRole.toLowerCase() === 'administrador',
        Ssn: filters.provider ? filters.provider.ssn : null,
        ProposalNumber: filters.proposalNumber,
        EvaluatorId: filters.evaluator ? filters.evaluator.id : null,
        RecruitmentTypeId: filters.recruitmentTypes
          ? filters.recruitmentTypes.id
          : null,
        ServiceTypesId: filters.serviceTypes ? filters.serviceTypes.id : null,
        EvaluationStatusId: this.isEvaluator
          ? filters.status
            ? filters.status.id
            : null
          : null,
        IsContractDocumentsCertified: filters.document
          ? filters.document.id
          : null,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pageSize,
      };

      await getProposalService(params).then(({ data }) => {
        this.results = [...data.data];
        this.pagination = {
          ...this.pagination,
          count: data.count,
          currentPage: data.currentPage,
          pageCount: data.pageCount,
          totalItemCount: data.totalItemCount,
        };
      });
    },
    goToEvaluators() {
      this.$router.push({
        name: 'ServiceProposalEvaluator',
      });
    },
    async assignProposalToEvaluator() {
      validateProposalsAssignedToEvaluators()
        .then(async (resp) => {
          if (resp.data) {
            const answer = await this.ShowCancelModal(
              'Confirmación',
              '¿Está seguro que desea continuar con la asignación de propuestas a evaluadores?',
              'Sí',
              'No'
            );

            if (!answer) {
              return;
            }

            this.$router.push({
              name: 'proposal-addevaluators',
            });
          }
        })
        .catch((err) => {
          this.ShowToast('Error', err.response.data.errors.errors[0]);
        });
    },
    async getEvaluatorsByYearId(yearId = '') {
      await getEvaluatorsByYearId(yearId).then((res) => {
        this.filters.evaluators = [...res.data];
      });
    },
    async getCompaniesByYearId(yearId) {
      await getCompaniesByYearId(yearId).then((res) => {
        this.filters.providers = [...res.data];
      });
    },
    async downloadSummaryEvaluationProcessReport() {
      const filters = { ...this.selectedFilters };

      const params = {
        academicYearId: filters.academicYear ? filters.academicYear.id : null,
      };
      await getSummaryEvaluationProcessReport(params).then((response) => {
        _generalApi.ConvertToBlob(response.data);
      });
    },
    async downloadEvaluatorProgressReport() {
      const filters = { ...this.selectedFilters };

      const params = {
        academicYearId: filters.academicYear ? filters.academicYear.id : null,
      };

      await getEvaluatorProgressReport(params).then((response) => {
        _generalApi.ConvertToBlob(response.data);
      });
    },
    advanceSearchHandler(value) {
      if (!value) {
        this.selectedFilters.area = null;
        this.selectedFilters.evaluator = null;
        this.selectedFilters.provider = null;
        this.selectedFilters.status = null;
        this.selectedFilters.document = null;
      }
    },
    async updateProposalServiceEvaluations() {
      let params = {
        academicYearId: this.$route.params.academicYearId,
      };
      await getProposalService(params).then(({ data }) => {
        this.results = [...data.data];
        this.pagination = {
          ...this.pagination,
          count: data.count,
          currentPage: data.currentPage,
          pageCount: data.pageCount,
          totalItemCount: data.totalItemCount,
        };
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/shared/scss/config/colors.scss';

::v-deep .reports-dropdown {
  .btn {
    color: map-get($colors, 'green');
    font-weight: normal;
    font-size: 1rem;
    margin-right: 0.5rem;
    padding: 3px 10px;
  }
}

@media only screen and (max-width: 768px) {
  ::v-deep .reports-dropdown {
    margin-bottom: 0.7143rem;
    margin-right: 0rem;
    .btn {
      width: 100%;
    }
  }
}

.service-proposal-history {
  &__filter-manager-wrapper--provider,
  &__filter-manager-wrapper--evaluator {
    .service-proposal-history__filter-manager ::v-deep {
      .ab-btn.success {
        width: auto;
        margin-left: 1.2rem;
        margin-right: 0;
        margin-top: -0.25rem;

        @media only screen and (max-width: 768px) {
          width: 86%;
        }
      }
    }
  }

  &__filter-manager-wrapper--provider {
    .service-proposal-history__filter-manager ::v-deep {
      width: 500px;

      @media only screen and (max-width: 768px) {
        width: auto;
      }
    }
    ::v-deep .service-proposal-history__filter-manager__search {
      margin: 0px !important;
    }
  }

  &__filter-manager ::v-deep {
    .ab-btn.success {
      margin-right: 20px;
      width: 86%;
    }
  }
}
</style>
